import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Divider, Switch } from 'src/@vymo/ui/atoms';
import Button from 'src/@vymo/ui/atoms/button';
import Text from 'src/@vymo/ui/atoms/text';
import { Body, Footer, Header, Modal } from 'src/@vymo/ui/blocks';
import { ReactComponent as GoogleIcon } from 'src/assets/icons/googleIcon.svg';
import { ReactComponent as LogoutIcon } from 'src/assets/icons/logoutLine.svg';
import { ReactComponent as OfficeIcon } from 'src/assets/icons/microsoftOffice.svg';
import { ReactComponent as AdminIcon } from 'src/assets/icons/userLine.svg';
import { ReactComponent as WebexIcon } from 'src/assets/icons/webex.svg';
import { ReactComponent as ZoomIcon } from 'src/assets/icons/zoom.svg';
import { getGrowthBookFeatureFlag } from 'src/featureFlags';
import { Keys, locale } from 'src/i18n';
import { getClientCode, getCurrentUser } from 'src/models/lmsConfig/queries';
import { USER_PROFILE } from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { renderModule } from 'src/workspace/slice';
import { getClientConfigData } from 'src/workspace/utils';
import { getTitleByType, getUserIntegrationAccounts } from '../queries';
import { getCalendarTokens } from '../selector';
import {
  disconnectAccountThunk,
  fetchAllTokens,
  logout,
  subscribeOutlook,
} from '../thunk';
import { CalendarToken, SelectedAccount, UserProfile } from './types';
import styles from './index.module.scss';

function Logout() {
  const config = getClientConfigData();
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | {}>(
    {},
  );

  const isUserProfileEnabled =
    _.get(config, 'user_profile_config') &&
    getGrowthBookFeatureFlag('enable-user-profile-platform');

  const [openAlertModal, setOpenAlertModal] = useState(false);
  const dispatch = useAppDispatch();
  const calendarTokens = useAppSelector((state) =>
    getCalendarTokens(state),
  ) as CalendarToken[];
  const integrations = getUserIntegrationAccounts();
  const user = getCurrentUser() as UserProfile;
  const [skipFetchOnMount, setSkipFetchOnMount] = useState(false);

  useEffect(() => {
    if (!skipFetchOnMount) {
      dispatch(fetchAllTokens());
    }
  }, [dispatch, skipFetchOnMount]);

  const disConnectAccount = (account: SelectedAccount) => {
    setSelectedAccount(account);
    setOpenAlertModal(true);
  };

  const onClickLogout = async () => {
    localStorage.clear();
    await dispatch(logout());
    window.location.href = '/';
  };

  const handleUserProfileNavigation = () => {
    dispatch(renderModule({ id: USER_PROFILE, props: {} }));
  };

  const closeAlertModal = async () => {
    if (!('type' in selectedAccount)) return;
    setSkipFetchOnMount(true);
    try {
      const result = await dispatch(
        disconnectAccountThunk(selectedAccount?.type as string),
      ).unwrap();

      // eslint-disable-next-line no-console
      console.log('Disconnect success:', result);
      setOpenAlertModal(false);
      window.location.reload();
    } catch (error) {
      setOpenAlertModal(false);
    }
  };

  const enableOutlookSync = async () => {
    const integrationsConfig = _.get(config, 'features_config');
    const outlookIntegrationsConfig = _.get(
      integrationsConfig,
      'outlook_integration',
    );
    const adminAuthEnabled = _.get(
      outlookIntegrationsConfig,
      'admin_auth_enabled',
    );
    if (adminAuthEnabled) {
      const result = await dispatch(subscribeOutlook()).unwrap();
      if (result?.error) {
        // eslint-disable-next-line no-console
        console.log(result?.error);
      } else {
        // eslint-disable-next-line no-console
        console.log('Connected Successfully');
      }
    } else {
      const outllookConfig = _.get(config, 'outlook_config');
      const {
        credentials = {},
        redirectUri = '',
        scopes = [],
      } = outllookConfig;
      const { clientID } = credentials;
      const scope = scopes.join(' ');
      const id = getClientCode();
      const uri =
        `${credentials.site + credentials.authorizationPath}?` +
        `client_id=${clientID}&response_type=code` +
        `&redirect_uri=${redirectUri}&response_mode=query&scope=${scope}&state=${id}`;
      window.location.href = uri;
    }
  };

  const enableZoomSync = () => {
    const zoomConfig = _.get(config, 'zoom_config');
    const credentials = _.get(zoomConfig, 'credentials');

    if (!zoomConfig || !credentials) {
      return;
    }

    const ZOOM_OAUTH_AUTH_URL = credentials.site;
    const id = getClientCode();
    const data = {
      response_type: 'code',
      client_id: credentials.clientID,
      redirect_uri: _.get(zoomConfig, 'redirectUri'),
      state: id,
    };
    const searchParams = new URLSearchParams(data);
    const uri = `${ZOOM_OAUTH_AUTH_URL}?${searchParams.toString()}`;
    window.location.href = uri;
  };

  const enableWebexSync = () => {
    const webexConfig = _.get(config, 'webex_config');
    const credentials = _.get(webexConfig, 'credentials');

    if (!webexConfig || !credentials) {
      return;
    }

    const WEBEX_OAUTH_AUTH_URL =
      credentials.oauth_url + credentials.authorizationPath;
    const id = getClientCode();
    const data = {
      response_type: 'code',
      client_id: credentials.clientID,
      redirect_uri: _.get(webexConfig, 'redirectUri'),
      state: id,
    };
    const searchParams = new URLSearchParams(data);
    const uri = `${WEBEX_OAUTH_AUTH_URL}&${searchParams.toString()}`;
    window.location.href = uri;
  };

  const enableGoogleSync = () => {
    const googleConfig = _.get(config, 'google_config');
    const credentials = _.get(googleConfig, 'credentials');

    if (!googleConfig || !credentials) {
      return;
    }

    const GOOGLE_OAUTH_AUTH_URL = credentials.site;
    const id = getClientCode();
    const scope = googleConfig.scopes?.join(' ');
    const data = {
      response_type: 'code',
      client_id: credentials.clientID,
      redirect_uri: _.get(googleConfig, 'redirectUri'),
      state: id,
      scope,
      access_type: 'offline',
      prompt: 'consent',
    };
    const searchParams = new URLSearchParams(data);
    const uri = `${GOOGLE_OAUTH_AUTH_URL}?${searchParams.toString()}`;
    window.location.href = uri;
  };

  const connectAccount = (type) => {
    dispatch(fetchAllTokens());
    if (type === 'outlook' && !_.find(calendarTokens, { type })) {
      enableOutlookSync();
    } else if (type === 'zoom') {
      enableZoomSync();
    } else if (type === 'webex') {
      enableWebexSync();
    } else if (type === 'google') {
      enableGoogleSync();
    }
  };

  function getAccountStatus(integrationType, index) {
    let account: SelectedAccount = {};
    let iconName = <GoogleIcon />;

    if (integrationType === 'outlook_integration') {
      account = { type: 'outlook', source: 'outlook' };
      iconName = <OfficeIcon />;
    } else if (integrationType === 'zoom_integration') {
      account = { type: 'zoom', source: 'zoom' };
      iconName = <ZoomIcon />;
    } else if (integrationType === 'webex_integration') {
      account = { type: 'webex', source: 'webex' };
      iconName = <WebexIcon />;
    } else if (integrationType === 'google_integration') {
      account = { type: 'google', source: 'google' };
    }

    const token = _.find(calendarTokens, { type: account.type });
    const isEnabled = !_.isEmpty(token);

    return (
      <div key={index}>
        <div className={styles.avatarDropdown__logins}>
          <div>{iconName}</div>

          <div className={styles.avatarDropdown__logins__description}>
            <Text
              classNames={
                styles.avatarDropdown__logins__description__accounttype
              }
            >
              {getTitleByType(account.type)}
            </Text>

            {token && (
              <Text
                classNames={styles.avatarDropdown__logins__description__text}
                type="subText"
              >
                {token.data.mail || token.data.email}
              </Text>
            )}
          </div>
          <Switch
            value={isEnabled}
            onChange={() => {
              const newVal = !isEnabled;
              if (newVal) {
                connectAccount(account.type);
              } else {
                disConnectAccount(account);
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.avatarDropdown}>
      <Button
        type="text"
        onClick={isUserProfileEnabled ? handleUserProfileNavigation : _.noop}
        iconProps={{ icon: <AdminIcon />, iconPosition: 'left' }}
        className={styles.avatarDropdown__account}
      >
        <div className={styles.avatarDropdown__account__text}>
          <Text type="label">My Profile</Text>
          <Text
            classNames={styles.avatarDropdown__logins__description__text}
            type="subText"
          >
            {user.email}
          </Text>
        </div>
      </Button>

      {integrations.length > 0 && <Divider />}

      {integrations.map((integrationType, i) =>
        getAccountStatus(integrationType, i),
      )}
      <Divider />
      <Button
        className={styles.avatarDropdown__logout}
        iconProps={{ icon: <LogoutIcon />, iconPosition: 'left' }}
        onClick={onClickLogout}
        type="text"
      >
        {locale(Keys.LOGOUT)}
      </Button>
      {openAlertModal && (
        <Modal>
          <Header>{locale(Keys.CONFIRMATION)}</Header>
          <Body>{locale(Keys.DISCONNECT_CONFIRMATION)}</Body>
          <Footer>
            <Button type="primary" onClick={() => closeAlertModal()}>
              {locale(Keys.CONFIRM)}
            </Button>
            <Button onClick={() => setOpenAlertModal(false)}>
              {locale(Keys.LEAVE)}
            </Button>
          </Footer>
        </Modal>
      )}
    </div>
  );
}

export default Logout;
